import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import type React from "react";

interface LoadingAnimationProps {
  size?: number;
  color?: string;
  type?: "dots" | "spinner" | "pulse" | "particles";
}

/**
 * custom loading animation component
 *
 * @param size - icon size
 * @param color - icon color
 * @param type - animation type
 */
const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  size = 40,
  color = "#1890ff",
  type = "spinner",
}) => {
  // custom loading icon
  const antIcon = <LoadingOutlined style={{ fontSize: size, color }} spin />;

  // dot loading animation
  if (type === "dots") {
    return (
      <div
        className="loading-dots"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <style>
          {`
            .loading-dots .dot {
              width: ${size / 3}px;
              height: ${size / 3}px;
              background-color: ${color};
              border-radius: 50%;
              margin: 0 ${size / 6}px;
              animation: dotPulse 1.5s infinite ease-in-out;
            }
            .loading-dots .dot:nth-child(2) {
              animation-delay: 0.2s;
            }
            .loading-dots .dot:nth-child(3) {
              animation-delay: 0.4s;
            }
            @keyframes dotPulse {
              0%, 100% { transform: scale(0.8); opacity: 0.6; }
              50% { transform: scale(1.2); opacity: 1; }
            }
          `}
        </style>
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
      </div>
    );
  }

  // pulse loading animation
  if (type === "pulse") {
    return (
      <div
        className="loading-pulse"
        style={{
          position: "relative",
          width: size,
          height: size,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>
          {`
            .loading-pulse .circle {
              position: absolute;
              border: 3px solid ${color};
              opacity: 1;
              border-radius: 50%;
              animation: pulseAnimate 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
            }
            .loading-pulse .circle:nth-child(2) {
              animation-delay: -0.5s;
            }
            @keyframes pulseAnimate {
              0% {
                top: 50%;
                left: 50%;
                width: 0;
                height: 0;
                opacity: 1;
                transform: translate(-50%, -50%);
              }
              100% {
                top: 50%;
                left: 50%;
                width: ${size}px;
                height: ${size}px;
                opacity: 0;
                transform: translate(-50%, -50%);
              }
            }
          `}
        </style>
        <div className="circle"></div>
        <div className="circle"></div>
      </div>
    );
  }

  // particles loading animation
  if (type === "particles") {
    const particleCount = 10;
    const particleSize = size / 8;
    const orbitRadius = size / 2;

    return (
      <div
        className="loading-particles"
        style={{
          position: "relative",
          width: size * 2,
          height: size * 2,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>
          {`
            .loading-particles {
              filter: blur(0.5px);
            }
            
            .loading-particles .particle {
              position: absolute;
              width: ${particleSize}px;
              height: ${particleSize}px;
              border-radius: 50%;
              background: linear-gradient(135deg, ${color}, #a855f7);
              box-shadow: 
                0 0 ${particleSize * 2}px ${color}80,
                0 0 ${particleSize * 4}px ${color}40,
                inset 0 0 ${particleSize / 2}px rgba(255, 255, 255, 0.8);
              animation: particleOrbit 3s cubic-bezier(0.45, 0, 0.55, 1) infinite;
              transform-origin: center center;
            }

            @keyframes particleOrbit {
              0% {
                transform: 
                  rotate(var(--angle)) 
                  translateX(${orbitRadius}px) 
                  scale(0.8);
                opacity: 0.6;
              }
              25% {
                transform: 
                  rotate(calc(var(--angle) + 90deg)) 
                  translateX(${orbitRadius * 1.2}px) 
                  scale(1.2);
                opacity: 1;
              }
              50% {
                transform: 
                  rotate(calc(var(--angle) + 180deg)) 
                  translateX(${orbitRadius}px) 
                  scale(0.8);
                opacity: 0.6;
              }
              75% {
                transform: 
                  rotate(calc(var(--angle) + 270deg)) 
                  translateX(${orbitRadius * 1.2}px) 
                  scale(1.2);
                opacity: 1;
              }
              100% {
                transform: 
                  rotate(calc(var(--angle) + 360deg)) 
                  translateX(${orbitRadius}px) 
                  scale(0.8);
                opacity: 0.6;
              }
            }

            .loading-particles .center-glow {
              position: absolute;
              width: ${particleSize * 2}px;
              height: ${particleSize * 2}px;
              border-radius: 50%;
              background: radial-gradient(circle, ${color}60, transparent 70%);
              animation: centerPulse 2s ease-in-out infinite;
            }

            @keyframes centerPulse {
              0%, 100% {
                transform: scale(1);
                opacity: 0.8;
              }
              50% {
                transform: scale(1.5);
                opacity: 0.4;
              }
            }
          `}
        </style>

        {/* Center glow effect */}
        <div className="center-glow" />

        {/* Generate particles */}
        {Array.from({ length: particleCount }).map((_, index) => {
          const angle = (360 / particleCount) * index;
          const delay = (index * 0.3) / particleCount;

          return (
            <div
              key={`particle-${angle}`}
              className="particle"
              style={{
                // @ts-ignore - CSS custom properties
                "--angle": `${angle}deg`,
                animationDelay: `${delay}s`,
              }}
            />
          );
        })}
      </div>
    );
  }

  // default use Ant Design Spin component
  return <Spin indicator={antIcon} />;
};

export default LoadingAnimation;
